import React from "react";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import TextInfo from "@/bit/components/components.text";
import IconGlyph from "@/bit/components/components.icon-glyph";
import CharacteristicItem from "./cms-characteristic-item";
import emptyContent from "./emptyContent.json";
import { getLayoutClassName, getContainerClassName } from "./utils";

const CmsAddElement = dynamic(() => import("@/bit/components/components.cms-block.cms-add-element"));
const ID_TEMPLATE_ORION = "253881";

/**
 * Componente que permite crear un Listado de características
 */

function CmsCharacteristicList(props) {
  const { blockName, alias, cms } = props;
  const columns = props.columns;
  const title = props["cms-atom-text-1"];
  let list = props["cms-ml-single-characteristic-list"];
  const cmsHandler = props?.cmsHandler;
  const layout = props?.layout || "default";
  const wrapElementsOnDivs = props?.wrapElementsOnDivs || cmsHandler || false;
  const className = props?.className;

  let finalColumns = null;
  if (columns && parseInt(columns) && parseInt(columns) !== 0) {
    finalColumns = parseInt(columns);
  }

  // Cuando no hay elementos en el editor
  if (cms && list.length === 0) {
    list = [emptyContent]
  }

  if (wrapElementsOnDivs) {
    return (
      <>
        <div
          id={blockName || ""}
          className={`${getContainerClassName(
            layout,
            "cms-characteristics-item"
          )}`}
        >
          {cmsHandler && (
            <span
              className={
                "element-editable block edit-component cms-characteristics-placeholder"
              }
              onClick={() =>
                cmsHandler({
                  ...props,
                  name: "cms-block.cms-characteristic-list",
                })
              }
            >
              <IconGlyph id="lapiz-m" height={20} color="white" />
            </span>
          )}

          <div className="cms-title-characteristics">
            {title && <TextInfo {...title} cmsHandler={cmsHandler} />}
          </div>
          <div
            className={`${getLayoutClassName(
              layout,
              "cms-characteristics-list"
            )}`}
            style={{ gridTemplateColumns: `repeat(${finalColumns || 5}, 1fr)` }}
          >
            {cmsHandler && (
              <CmsAddElement
                addProps={{
                  index: "start",
                  idTemplateOrion: ID_TEMPLATE_ORION,
                  itemIdDotNotation: `${blockName}${layout === "default" ? ".cms-ml-characteristics-1" : ""}.cms-ml-single-characteristic-list`,
                  alias,
                  layout: layout === "default" ? "vertical" : "horizontal",
                  small: layout === "default",
                }}
                cmsHandler={cmsHandler}
              />
            )}
            {list &&
              Array.isArray(list) &&
              list.map((itemProps, index) => {
                const characTitle = itemProps["cms-atom-text-1"];
                const characDescp = itemProps["cms-atom-text-2"];
                const characIcon = itemProps["cms-atom-icon-1"];
                const characLabel = itemProps["cms-atom-label-1"];

                return (
                  <CharacteristicItem
                    title={characTitle}
                    description={characDescp}
                    icon={characIcon}
                    label={characLabel}
                    cmsHandler={cmsHandler}
                    layout={layout}
                    alias={alias}
                    blockName={blockName}
                    index={index}
                    frontalListToDelete={list}
                  />
                );
              })}
            {cmsHandler && (
              <CmsAddElement
                addProps={{
                  index: "end",
                  idTemplateOrion: ID_TEMPLATE_ORION,
                  itemIdDotNotation: `${blockName}${layout === "default" ? ".cms-ml-characteristics-1" : ""}.cms-ml-single-characteristic-list`,
                  alias,
                  layout: layout === "default" ? "vertical" : "horizontal",
                  small: layout === "default",
                }}
                cmsHandler={cmsHandler}
              />
            )}
          </div>
        </div>
        <style jsx>{`.cms-characteristics-item {
  background-color: white;
  position: relative;
  &-grid {
    margin: 2rem 0;
  }
  .cms-title-characteristics {
    margin-bottom: 1rem;
  }
  .cms-characteristics-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    
    &-grid {
      display: grid;
      justify-content: space-between;
      gap: 4rem;
      row-gap: 3rem;
    }
  }
}

@media screen and (max-width: 992px) {
  .cms-characteristics-item {
    .cms-characteristics-list {
      &-grid {
        gap: 1rem;
      }
    }
  }
}

@media screen and (max-width: 992px) {
  .cms-characteristics-item {
    .cms-characteristics-list {
      &-grid {
        display: flex;
        flex-flow: row wrap;
        justify-content: space-around;
      }
    }
  }
}
`}</style>
      </>
    );
  }
  /*
   render para invocar desde el cms-module-characteristics-and-steps o antiguo fibre-pros-section
    permite que se queden elementos suelos para que un grid superior lso alinee
  */
  return (
    <>
      {title && (
        <TextInfo {...title} cmsHandler={cmsHandler} className={className} />
      )}
      {list &&
        Array.isArray(list) &&
        list.map((itemProps) => {
          const characTitle = itemProps["cms-atom-text-1"];
          const characDescp = itemProps["cms-atom-text-2"];
          const characIcon = itemProps["cms-atom-icon-1"];
          const characLabel = itemProps["cms-atom-label-1"];
          return (
            <CharacteristicItem
              title={characTitle}
              description={characDescp}
              icon={characIcon}
              label={characLabel}
              cmsHandler={cmsHandler}
              layout={layout}
              className={className}
              frontalListToDelete={list}
            />
          );
        })}
    </>
  );
}

export default CmsCharacteristicList;

CmsCharacteristicList.propTypes = {
  /**
   * {
   *  "info": "Layour del componente. Controla la distribución de los elementos.",
   *  "kind": "both",
   *  "beautifulName": "Layout",
   *  "values": "./enums.js:LAYOUT_TYPES"
   * }
   */
  layout: PropTypes.oneOf(["default", "grid"]),
  /**
   * {
   *  "info": "Permite que se queden elementos sueltos para que un grid superior los alinee cuando es false",
   *  "kind": "dev",
   *  "beautifulName": "wrapElementsOnDivs"
   * }
   */
  wrapElementsOnDivs: PropTypes.bool,
  /**
   * {
   *  "info": "Si no es nulo, obliga al grid a tener unas columnas especificas",
   *  "kind": "both",
   *  "beautifulName": "Número de columnas (0 si no se quiere especificar)"
   * }
   */
  columns: PropTypes.number,
};

CmsCharacteristicList.defaultProps = {
  layout: "default",
  wrapElementsOnDivs: true,
  columns: 0,
};
